/**
 * API client — single-flight refresh interceptor and authenticated fetch helper.
 *
 * All auth-domain API calls use direct fetch (see features/[domain]/api.ts).
 * The apiFetch helper below injects the Bearer token and handles automatic
 * token refresh on 401.
 */
import { useAuthStore, type Subject } from '../../stores/auth.js';
import { ApiError, NetworkError, apiErrorFromResponse } from './error.js';
import { tenantHeaderValue, tenantReady } from '../../stores/tenant.js';
import { stashForcedLogout } from './forced-logout.js';
import { isImpersonationTab } from '../impersonation-bootstrap.js';
import { apiRequest, consumeHostMoved } from './host-pool.js';

/** Client-side code for a session lost to an API host switch; catalogued in errors.json. */
export const API_HOST_MOVED = 'API-HOST-MOVED';

export const __testing__refreshState: { inflight: Promise<string> | null; reset: () => void } = {
  inflight: null,
  reset() {
    this.inflight = null;
  },
};

async function _fetchMeWithToken(accessToken: string): Promise<Subject | null> {
  try {
    await tenantReady();
    const tid = tenantHeaderValue();
    const headers: Record<string, string> = { authorization: `Bearer ${accessToken}` };
    if (tid) headers['x-tenant-id'] = tid;
    const res = await apiRequest(`/members/me`, {
      credentials: 'include',
      headers,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      member?: { id?: string; agentId?: string; username?: string };
    };
    const m = data.member;
    if (!m?.id) return null;
    return {
      id: m.id,
      kind: 'player',
      agentId: m.agentId ?? '',
      currency: 'THB',
      displayName: m.username,
    };
  } catch {
    return null;
  }
}

async function _doRefreshOnce(): Promise<string> {
  const { refreshToken } = useAuthStore.getState();

  // An impersonation tab holds a short-lived, non-renewable staff token and
  // never has a refresh token of its own. The refresh cookie, by contrast, is
  // origin-wide — letting this tab refresh would silently upgrade a read-only
  // "view as member" session into whatever real member session the browser
  // happens to hold. Expire instead.
  if (isImpersonationTab()) {
    useAuthStore.getState().clear();
    throw new Error('impersonation session cannot refresh');
  }

  let res: Response;
  try {
    await tenantReady();
    const tid = tenantHeaderValue();
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    if (tid) headers['x-tenant-id'] = tid;
    // Body token is legacy/native-client only. Web sessions authenticate the
    // rotation with the HttpOnly cookie, which `credentials: 'include'` sends.
    res = await apiRequest(`/auth/refresh`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });
  } catch (cause) {
    throw new NetworkError((cause as Error).message);
  }

  if (!res.ok) {
    // The last hand-rolled `{ code }` reader. Reading the refusal through the
    // one parser is what lets its extensions reach the catalog's `_detailed`
    // keys — they were dropped here even after #221 collapsed the other three.
    const err = await apiErrorFromResponse(res, 'refresh failed');
    // Stash the refusal so the login page can show a specific reason (e.g.
    // "signed in on another device") — clear() below wipes the auth store,
    // and the redirect that follows leaves no live component to toast from.
    // After a host switch the refresh cookie stayed with the old API host, and
    // that, not the server's refusal, is what the player needs to be told.
    // Only the refusal that ends a live session names the reason: a request
    // that left with the old token before clear() gets its 401 afterwards,
    // refreshes again and is refused again, and must not overwrite it.
    if (useAuthStore.getState().accessToken !== null) {
      stashForcedLogout(
        consumeHostMoved() ? new ApiError({ status: 401, code: API_HOST_MOVED, message: 'api host moved' }) : err,
      );
    }
    useAuthStore.getState().clear();
    throw err;
  }

  const data = (await res.json()) as {
    accessToken: string;
    refreshToken?: string;
    subject?: Subject;
    subjectKind?: string;
    sessionId?: string;
  };

  // The session survived whatever host move came before, so a later logout is not about it.
  consumeHostMoved();
  useAuthStore.getState().setAuth({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken ?? null,
    ...(data.subject ? { subject: data.subject } : {}),
  });

  if (!data.subject) {
    const subject = await _fetchMeWithToken(data.accessToken);
    if (subject) {
      useAuthStore.getState().setSubject(subject);
    }
  }

  return data.accessToken;
}

export function __testing__doRefresh(): Promise<string> {
  __testing__refreshState.inflight ??= _doRefreshOnce().finally(() => {
    __testing__refreshState.inflight = null;
  });
  return __testing__refreshState.inflight;
}

/**
 * Public alias for the single-flight refresh.
 * Used by useAutoLogout to proactively refresh when the access token is
 * detected as expired on tab focus.
 */
export const triggerRefresh = __testing__doRefresh;

/**
 * Authenticated fetch with automatic single-flight refresh on 401.
 *
 * Usage:
 *   const data = await apiFetch<WalletBalances>('/wallets/me');
 */
export async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const makeRequest = async (token: string | null): Promise<Response> => {
    await tenantReady();
    const headers = new Headers(options?.headers);
    if (token) headers.set('authorization', `Bearer ${token}`);
    // Per-brand tenant header — sent ONLY in override mode (VITE_TENANT_ID set).
    // In the default Host-resolution path the gateway derives the tenant from
    // the origin host, so we must NOT send X-Tenant-Id.
    const tid = tenantHeaderValue();
    if (tid && !headers.has('x-tenant-id')) headers.set('x-tenant-id', tid);
    return apiRequest(`${path}`, {
      credentials: 'include',
      ...options,
      headers,
    });
  };

  const { accessToken } = useAuthStore.getState();
  let res: Response;
  try {
    res = await makeRequest(accessToken);
  } catch (cause) {
    throw new NetworkError((cause as Error).message);
  }

  // Only attempt token refresh if we actually sent a token. A 401 on an
  // unauthenticated request (guest hitting an auth-only endpoint, e.g. the
  // recent-games feed) is expected and must NOT trigger clear() — clear() wipes
  // the whole query cache, which would blank out public pages (casino/lobby)
  // every time a background guest request 401s.
  if (res.status === 401 && accessToken) {
    let newToken: string;
    try {
      newToken = await __testing__doRefresh();
    } catch (cause) {
      useAuthStore.getState().clear();
      // Carry the refusal's code and extensions out to the caller. Replacing
      // them with a code-less error here is what put every session-ended toast
      // on the generic string, however well the refusal named its reason.
      //
      // The status stays 401 rather than the refusal's own: the session is gone
      // by the time this throws (clear() above, and _doRefreshOnce before it),
      // and callers that branch on 401 — mapPlayError's relogin CTA, for one —
      // are reading exactly that fact, not the refresh endpoint's HTTP verdict.
      if (cause instanceof ApiError) {
        throw new ApiError({
          status: 401,
          code: cause.code,
          message: cause.message,
          extensions: cause.extensions,
        });
      }
      // A refresh that never reached the server (network, or the impersonation
      // guard) has no refusal to carry — and must not be reported as one.
      throw new ApiError({ status: 401, message: 'session expired' });
    }
    try {
      res = await makeRequest(newToken);
    } catch (cause) {
      throw new NetworkError((cause as Error).message);
    }
  }

  if (!res.ok) throw await apiErrorFromResponse(res, 'Request failed');

  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}
